/**
 * ShareActions - Kontekstowy komponent "Udostępnij"
 * NIE jest osobną stroną - wpleciony w widok "Edytuj stronę/ofertę"
 * 
 * Opcje: Link (UTM), QR Kod, Facebook, Ogłoszenia
 * Język: Uniwersalny (firma/usługi, nie hodowla)
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { 
  Link2, QrCode, Facebook, FileText, Copy, Check, Download,
  Rocket, ExternalLink, Share2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';

const SITE_URL = 'https://apisai.pl';

export default function ShareActions({ 
  type = 'kennel', // kennel, litter, stud, offer
  slug,
  name,
  breed,
  city,
  description,
  boosterActive = false,
  className = '',
  onShare = null // callback when share happens
}) {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);

  // Track share event
  const trackShare = async (eventType) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      await axios.post(`${API}/share/track`, {
        event_type: eventType,
        entity_type: type,
        entity_id: slug
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Callback to parent
      if (onShare) onShare(eventType);
    } catch (e) {
      // Silent fail - don't interrupt user
    }
  };

  // Build URL with UTM
  const getPageUrl = (source = 'link') => {
    const typeMap = {
      kennel: 'hodowla',
      litter: 'miot',
      stud: 'reproduktor'
    };
    const path = typeMap[type] || 'hodowla';
    return `${SITE_URL}/${path}/${slug}?utm_source=${source}&utm_medium=social&utm_campaign=share`;
  };

  const copyToClipboard = async (text, id, eventType = 'link_copy') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success('Skopiowano do schowka!');
      trackShare(eventType);
    } catch (e) {
      toast.error('Błąd kopiowania');
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById('share-qr-code');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = 512;
      canvas.height = 512;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `${slug}-qrcode.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    toast.success('QR kod pobrany!');
    trackShare('qr_download');
  };

  // Generate universal description for ads (not breed-specific)
  const getAdDescription = () => {
    const baseDesc = description || `Profesjonalna oferta - ${name}`;
    return `${baseDesc}

📍 Lokalizacja: ${city || 'Polska'}
🌐 Więcej informacji: ${getPageUrl('ads')}

Serdecznie zapraszam do kontaktu!`;
  };

  const pageUrl = getPageUrl('link');

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Action Cards Grid - matching success modal style */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* 1. Link Card */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
          <div className="w-10 h-10 mb-3 rounded-full bg-blue-500 flex items-center justify-center">
            <Link2 className="w-5 h-5 text-white" />
          </div>
          <h4 className="font-semibold text-zinc-900 mb-1">Link</h4>
          <p className="text-sm text-zinc-600 mb-3">Skopiuj link do swojej strony</p>
          
          <div className="flex items-center gap-2 p-2 bg-white/70 rounded-lg mb-3">
            <span className="text-xs text-zinc-500 truncate flex-1">{pageUrl.substring(0, 40)}...</span>
          </div>
          
          <Button
            onClick={() => copyToClipboard(pageUrl, 'link')}
            className={`w-full rounded-lg text-sm font-medium ${
              copiedId === 'link' 
                ? 'bg-green-500 hover:bg-green-600' 
                : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
          >
            {copiedId === 'link' ? (
              <><Check className="w-4 h-4 mr-2" /> Skopiowano</>
            ) : (
              <><Copy className="w-4 h-4 mr-2" /> Kopiuj link</>
            )}
          </Button>
        </div>

        {/* 2. QR Code Card */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200">
          <div className="w-10 h-10 mb-3 rounded-full bg-purple-500 flex items-center justify-center">
            <QrCode className="w-5 h-5 text-white" />
          </div>
          <h4 className="font-semibold text-zinc-900 mb-1">QR Kod</h4>
          <p className="text-sm text-zinc-600 mb-3">Pobierz QR kod do druku lub online</p>
          
          <div className="flex justify-center mb-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <QRCodeSVG 
                id="share-qr-code"
                value={getPageUrl('qr')}
                size={64}
                level="M"
                includeMargin={false}
              />
            </div>
          </div>
          
          <Button
            onClick={downloadQR}
            className="w-full rounded-lg text-sm font-medium bg-purple-500 hover:bg-purple-600 text-white"
          >
            <Download className="w-4 h-4 mr-2" /> Pobierz QR
          </Button>
        </div>

        {/* 3. Facebook Card */}
        <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl p-5 border border-sky-200">
          <div className="w-10 h-10 mb-3 rounded-full bg-sky-500 flex items-center justify-center">
            <Facebook className="w-5 h-5 text-white" />
          </div>
          <h4 className="font-semibold text-zinc-900 mb-1">Facebook</h4>
          <p className="text-sm text-zinc-600 mb-3">Udostępnij na swoim profilu lub fanpage</p>
          
          <div className="flex gap-2">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getPageUrl('facebook'))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
              onClick={() => trackShare('facebook_share')}
            >
              <Button className="w-full rounded-lg text-sm font-medium bg-sky-500 hover:bg-sky-600 text-white">
                <ExternalLink className="w-4 h-4 mr-2" /> Udostępnij
              </Button>
            </a>
          </div>
        </div>

        {/* 4. Ads / Ogłoszenia Card */}
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-5 border border-emerald-200">
          <div className="w-10 h-10 mb-3 rounded-full bg-emerald-500 flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <h4 className="font-semibold text-zinc-900 mb-1">Ogłoszenia</h4>
          <p className="text-sm text-zinc-600 mb-3">Gotowy opis do portali ogłoszeniowych</p>
          
          <Button
            onClick={() => copyToClipboard(getAdDescription(), 'ads', 'ads_copy')}
            className={`w-full rounded-lg text-sm font-medium ${
              copiedId === 'ads' 
                ? 'bg-green-500 hover:bg-green-600' 
                : 'bg-emerald-500 hover:bg-emerald-600'
            } text-white`}
          >
            {copiedId === 'ads' ? (
              <><Check className="w-4 h-4 mr-2" /> Skopiowano</>
            ) : (
              <><Copy className="w-4 h-4 mr-2" /> Kopiuj opis</>
            )}
          </Button>
        </div>
      </div>

      {/* Booster CTA */}
      <div className="mt-4 p-4 bg-gradient-to-r from-[#F7931E]/10 to-[#F7931E]/20 rounded-xl border border-[#F7931E]/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F7931E] flex items-center justify-center flex-shrink-0">
            <Rocket className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-zinc-900 font-medium">
              Chcesz dotrzeć do większej liczby klientów?
            </p>
            <p className="text-xs text-zinc-600">
              {boosterActive 
                ? 'Twój Booster jest aktywny!' 
                : 'Uruchom Booster i promuj automatycznie.'}
            </p>
          </div>
          <Button
            onClick={() => navigate(boosterActive ? '/panel/booster/wyniki' : '/panel/booster-v2-new')}
            className="rounded-lg text-sm font-medium bg-[#F7931E] hover:bg-[#E07F0A] text-white"
          >
            {boosterActive ? 'Wyniki' : 'Booster'}
          </Button>
        </div>
      </div>
    </div>
  );
}
